import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { RichTextEditor } from '../../../components/ui/rich-text-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';

interface Standort {
  id: number;
  name: string;
  slug: string;
  code?: string | null;
  kuerzel?: string;
  beschreibung?: string;
  adresse?: string;
  telefon?: string;
  email?: string;
  website?: string;
  impressum_html?: string;
  trial_forward_legacy?: boolean;
  stammdaten_land_sichtbar?: boolean;
  legacy_schule_id?: string;
  aktiv: boolean;
  is_active?: boolean;
}

const defaultForm = (): Partial<Standort> => ({
  name: '',
  slug: '',
  kuerzel: '',
  beschreibung: '',
  adresse: '',
  telefon: '',
  email: '',
  website: '',
  impressum_html: '',
  trial_forward_legacy: false,
  stammdaten_land_sichtbar: false,
  legacy_schule_id: '',
  aktiv: true,
});

const normalizeStandort = (input: Record<string, unknown>): Standort => {
  const isActive = typeof input.aktiv === 'boolean'
    ? input.aktiv
    : (typeof input.is_active === 'boolean' ? input.is_active : true);

  return {
    id: Number(input.id || 0),
    name: String(input.name || ''),
    slug: typeof input.slug === 'string' ? input.slug : String(input.code || ''),
    code: typeof input.code === 'string' ? input.code : null,
    kuerzel: typeof input.kuerzel === 'string' ? input.kuerzel : '',
    beschreibung: typeof input.beschreibung === 'string' ? input.beschreibung : '',
    adresse: typeof input.adresse === 'string' ? input.adresse : '',
    telefon: typeof input.telefon === 'string' ? input.telefon : '',
    email: typeof input.email === 'string' ? input.email : '',
    website: typeof input.website === 'string' ? input.website : '',
    impressum_html: typeof input.impressum_html === 'string' ? input.impressum_html : '',
    trial_forward_legacy: input.trial_forward_legacy === true,
    stammdaten_land_sichtbar: input.stammdaten_land_sichtbar === true,
    legacy_schule_id: typeof input.legacy_schule_id === 'string' ? input.legacy_schule_id : '',
    aktiv: isActive,
    is_active: isActive,
  };
};

const TenantEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = id === 'new' || !id;
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [isSaving, setIsSaving] = useState(false);
  const [selected, setSelected] = useState<Standort | null>(null);
  const [form, setForm] = useState<Partial<Standort>>(defaultForm());

  useEffect(() => {
    if (isCreate) {
      setSelected(null);
      setForm(defaultForm());
      setIsLoading(false);
      return;
    }
    const standortId = Number(id);
    if (!Number.isFinite(standortId) || standortId <= 0) {
      toast.error('Ungültige Standort-ID');
      navigate('/system/organization/tenants');
      return;
    }

    setIsLoading(true);
    api.get<Standort>(`/api/standorte/${standortId}`)
      .then((data) => {
        const normalized = normalizeStandort(data as unknown as Record<string, unknown>);
        setSelected(normalized);
        setForm({ ...normalized });
      })
      .catch((e) => {
        console.error(e);
        toast.error('Standort konnte nicht geladen werden');
        navigate('/system/organization/tenants');
      })
      .finally(() => setIsLoading(false));
  }, [id, isCreate, navigate]);

  const pageTitle = useMemo(
    () => (isCreate ? 'Neuen Standort anlegen' : `Standort bearbeiten: ${selected?.name || ''}`),
    [isCreate, selected?.name],
  );

  const handleSave = async () => {
    if (!form.name || form.name.trim() === '') {
      toast.error('Name ist erforderlich');
      return;
    }
    const kuerzel = String(form.kuerzel || '').trim().toUpperCase();
    if (!/^[A-ZÄÖÜ]*$/.test(kuerzel)) {
      toast.error('Das Kürzel darf nur Buchstaben enthalten');
      return;
    }
    const payload = {
      name: String(form.name || '').trim(),
      code: String(form.slug || '').trim() || null,
      kuerzel: kuerzel || null,
      is_active: Boolean(form.aktiv),
      beschreibung: form.beschreibung ?? '',
      adresse: form.adresse ?? '',
      telefon: form.telefon ?? '',
      email: form.email ?? '',
      website: form.website ?? '',
      impressum_html: form.impressum_html ?? '',
      trial_forward_legacy: Boolean(form.trial_forward_legacy),
      stammdaten_land_sichtbar: Boolean(form.stammdaten_land_sichtbar),
      legacy_schule_id: String(form.legacy_schule_id || '').trim(),
    };

    setIsSaving(true);
    try {
      if (selected?.id) {
        const updatedRaw = await api.patch<Standort>(`/api/standorte/${selected.id}`, payload);
        const updated = normalizeStandort(updatedRaw as unknown as Record<string, unknown>);
        setSelected(updated);
        setForm({ ...updated });
        toast.success('Standort aktualisiert');
      } else {
        const created = await api.post<Standort>('/api/standorte', payload);
        toast.success('Standort erstellt');
        navigate(`/system/organization/tenants/${created.id}`, { replace: true });
      }
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error && e.message ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">{pageTitle}</h1>
          <p className="text-muted-foreground">
            Basisdaten des Standorts. Website-Inhalte, Bilder, Team &amp; SEO werden im CMS gepflegt.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/system/organization/tenants')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zur Liste
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            Speichern
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Name</Label>
          <Input uiSize="md" value={form.name || ''} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Slug</Label>
          <Input uiSize="md" value={form.slug || ''} onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))} placeholder="automatisch aus Name, optional" />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label htmlFor="standortKuerzel">Kürzel</Label>
          <p className="text-xs text-muted-foreground">
            Das Kürzel aus dem Altsystem, z. B. „BS“. Damit findet Sidigo Altnummern wie „BS-12345“.
            Jedes Kürzel gibt es nur einmal.
          </p>
          <Input
            id="standortKuerzel"
            uiSize="md"
            value={form.kuerzel || ''}
            maxLength={8}
            onChange={(e) => setForm((prev) => ({ ...prev, kuerzel: e.target.value.toUpperCase() }))}
            placeholder="z. B. BS"
          />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Adresse</Label>
          <Input uiSize="md" value={form.adresse || ''} onChange={(e) => setForm((prev) => ({ ...prev, adresse: e.target.value }))} />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Telefon</Label>
          <Input uiSize="md" value={form.telefon || ''} onChange={(e) => setForm((prev) => ({ ...prev, telefon: e.target.value }))} />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>E-Mail</Label>
          <Input uiSize="md" type="email" value={form.email || ''} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Website</Label>
          <Input uiSize="md" value={form.website || ''} onChange={(e) => setForm((prev) => ({ ...prev, website: e.target.value }))} />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Kurzbeschreibung (intern/basis)</Label>
          <Textarea density="comfortable" value={form.beschreibung || ''} onChange={(e) => setForm((prev) => ({ ...prev, beschreibung: e.target.value }))} />
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Impressum (standortspezifisch)</Label>
          <p className="text-xs text-muted-foreground">
            Optionales, eigenes Impressum für diesen Standort. Leer lassen, um das allgemeine
            Impressum der Website (CMS &gt; Rechtstexte) zu verwenden.
          </p>
          <RichTextEditor
            value={form.impressum_html || ''}
            onChange={(html) => setForm((prev) => ({ ...prev, impressum_html: html }))}
          />
        </div>
        <div className="grid gap-3 rounded-md border p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label>Feld „Land" in den Stammdaten zeigen</Label>
              <p className="text-xs text-muted-foreground">
                Aus bleibt es, solange dieser Standort nur in Deutschland unterrichtet — dann wird
                Deutschland stillschweigend gesetzt. An bringt das Feld in jedes Kundenformular.
              </p>
            </div>
            <Switch
              checked={Boolean(form.stammdaten_land_sichtbar)}
              onCheckedChange={(v: boolean) => setForm((prev) => ({ ...prev, stammdaten_land_sichtbar: v }))}
            />
          </div>
        </div>
        <div className="grid gap-3 rounded-md border p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label>Probestunden an Legacy-System senden</Label>
              <p className="text-xs text-muted-foreground">
                Während der Migration: Neue Probestunden-Anfragen dieses Standorts werden an das
                alte System weitergeleitet und dort bearbeitet. In Sidigo bleibt die Anfrage mit
                dem Stand „An Altsystem weitergeleitet“ erhalten.
              </p>
            </div>
            <Switch
              checked={Boolean(form.trial_forward_legacy)}
              onCheckedChange={(v: boolean) => setForm((prev) => ({ ...prev, trial_forward_legacy: v }))}
            />
          </div>
          {form.trial_forward_legacy && (
            <div className="grid gap-1.5">
              <Label>Legacy-Standort-ID (schule)</Label>
              <Input
                uiSize="md"
                value={form.legacy_schule_id || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, legacy_schule_id: e.target.value }))}
                placeholder="ID des Standorts im alten System"
              />
              <p className="text-xs text-muted-foreground">
                Erforderlich für die Weiterleitung. Ohne gültige ID bleibt die Anfrage in Sidigo.
              </p>
            </div>
          )}
        </div>
        <div className="grid gap-2 rounded-md border p-4">
          <Label>Status</Label>
          <Select value={form.aktiv ? 'true' : 'false'} onValueChange={(v: string) => setForm((prev) => ({ ...prev, aktiv: v === 'true' }))}>
            <SelectTrigger size="md">
              <SelectValue placeholder="Status auswählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Aktiv</SelectItem>
              <SelectItem value="false">Inaktiv</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default TenantEditorPage;
